/** Milky PVC doorway curtain, with deterministic individual strips for beat one. */
import * as THREE from 'three';
import { surface } from '../materials';
import type { AssetDefinition } from '../types';

const STRIP_COLOURS = [0xcccbc0, 0xd5d4c8, 0xdddcd0, 0xe6e5d8] as const;

/** Deterministic 0..1 from an integer. A seeded factory is still a pure one. */
function hash(n: number): number {
  let x = (n ^ 0x9e3779b9) >>> 0;
  x = Math.imul(x ^ (x >>> 16), 0x21f0aaad) >>> 0;
  x = Math.imul(x ^ (x >>> 15), 0x735a2d97) >>> 0;
  return ((x ^ (x >>> 15)) >>> 0) / 4294967296;
}

function stripMaterialIndex(variation: number): 0 | 1 | 2 | 3 {
  if (variation < 0.25) return 0;
  if (variation < 0.5) return 1;
  if (variation < 0.75) return 2;
  return 3;
}

/**
 * Builds a still curtain or a render-controlled passing sway.
 *
 * There are only four quantized albedo materials for all eleven strips. A unique
 * material for each slight tint variation would turn one doorway into eleven draw
 * calls, needlessly spending the frame's 400-call budget.
 */
export function stripCurtain(sway: number): THREE.Group {
  const group = new THREE.Group();
  group.name = 'strip-curtain';
  const railMaterial = surface('metal', 0x9aa3a5);
  // PVC uses organic because Rosalie's monotonic D-002 wear parameter must never
  // make this doorway age.
  const stripMaterials = STRIP_COLOURS.map((colour) => surface('organic', colour));

  const header = new THREE.Mesh(new THREE.BoxGeometry(1, 0.05, 0.055), railMaterial);
  header.name = 'galvanized-header-rail';
  header.position.set(0, 2.075, 0);
  header.castShadow = true;
  group.add(header);

  for (let index = 0; index < 11; index += 1) {
    const variation = hash(index);
    const displaced = index === 4 || index === 5;
    const centreWeight = 1 - Math.abs(index - 5) / 5;
    const length = displaced ? 2.0 : 2.05;
    const yaw = displaced ? 0.3 : (variation - 0.5) * 0.26;
    const lean = (variation - 0.5) * 0.1 * centreWeight + sway * 0.22;
    const yawEdge = 0.05 * (1 - Math.abs(Math.cos(yaw))) + 0.002 * Math.abs(Math.sin(yaw));
    const edgeInset = (1 - centreWeight) * yawEdge * Math.sign(index - 5);
    const x = (index - 5) * 0.09 + (variation - 0.5) * 0.016 * centreWeight - edgeInset;
    const material = stripMaterials[stripMaterialIndex(variation)];
    if (material === undefined) throw new Error('Missing PVC strip material');

    const strip = new THREE.Mesh(new THREE.BoxGeometry(0.1, length, 0.004), material);
    strip.name = `pvc-strip-${index + 1}`;
    strip.rotation.set(0, yaw, lean);
    strip.position.x = x;
    // Long strips are translated back onto the ground after their small lean.
    // Keeping the attachment end very slightly inside the header is preferable to
    // a sub-millimetre floor penetration that violates the placement contract.
    if (displaced) {
      strip.position.y = 2.05 - length / 2;
    } else {
      const lowerLocalY = -length / 2 * Math.cos(lean) - 0.05 * Math.abs(Math.sin(lean));
      strip.position.y = -lowerLocalY;
    }
    strip.castShadow = true;
    group.add(strip);
  }

  return group;
}

function build(): THREE.Group {
  return stripCurtain(0);
}

export const stripCurtainAsset: AssetDefinition = {
  id: 'strip-curtain',
  label: 'PVC strip curtain',
  description: 'Milky plastic livestock doorway curtain with a recently disturbed central gap.',
  footprint: { width: 1.0, height: 2.1, length: 0.06 },
  create: build,
};
